import {
  UserData,
  UserProvisioningResponse,
  UserStatus,
  UserTransferRequest,
  UserTransferResult
} from "@/entities/users/types";
import { TransferData, TRANSLATION_KEY } from "./hooks/types";

// The counts the modal shows treat a user as an owner through any of these columns, so all of
// them have to move — otherwise fewer records change hands than the admin was told.
const TRANSFER_FIELDS: Record<keyof TransferData, readonly string[]> = {
  companies: ["Companies.sdrOwner", "Companies.aeOwner", "Companies.csmOwner"],
  deals: ["Deals.managedBy"],
  leads: ["Leads.aeOwner", "Leads.sdrOwner", "Leads.tokenHolder"],
  newReportingManager: ["Users.managerId"]
};

// Signals an unreadable response rather than a field the endpoint named. Kept distinct from a
// real column key so a caller can never mistake it for one.
export const UNKNOWN_TRANSFER_FAILURE = "unknown";

export class TransferFailedError extends Error {
  readonly failedFields: string[];
  // The endpoint commits the fields that succeeded even when others fail, so a caller cannot
  // tell the admin "nothing moved" without knowing whether anything did.
  readonly movedAnyRecords: boolean;

  constructor(failedFields: string[], movedAnyRecords: boolean) {
    super(`Failed to transfer records: ${failedFields.join(", ")}`);
    this.name = "TransferFailedError";
    this.failedFields = failedFields;
    this.movedAnyRecords = movedAnyRecords;
  }
}

export const buildTransferPayload = (
  fromIds: string[],
  transferData: TransferData
): UserTransferRequest | null => {
  const toIds: Record<string, string> = {};

  Object.entries(TRANSFER_FIELDS).forEach(([entity, fields]) => {
    const toUserId = transferData[entity as keyof TransferData];
    if (!toUserId) return;
    fields.forEach((field) => {
      toIds[field] = toUserId;
    });
  });

  if (fromIds.length === 0 || Object.keys(toIds).length === 0) {
    return null;
  }

  return { fromIds, toIds };
};

// The endpoint answers 200 with a populated failedTransfers instead of raising, so awaiting it
// is not enough to know the records actually moved. A body that does not carry the map at all is
// treated as a failure: this gates the status flip, and an unreadable answer must not read as
// success or the user is deactivated with their records left behind.
export const getTransferFailures = (result: UserTransferResult): string[] => {
  const failures = result?.failedTransfers;
  if (!failures || typeof failures !== "object" || Array.isArray(failures)) {
    return [UNKNOWN_TRANSFER_FAILURE];
  }
  return Object.keys(failures);
};

// A partial transfer commits the fields that succeeded, so the admin must not be told nothing
// happened. Unknown counts resolve to "moved" rather than "not moved": the Spanner dialect sets
// supports_sane_rowcount = False and its cursor yields -1 or null when it cannot report a count,
// and claiming nothing changed on a transfer that may have committed is the failure this exists
// to prevent. Only an explicit 0 means the field matched no rows.
export const didTransferMoveRecords = (result: UserTransferResult): boolean => {
  const succeeded = result?.successfulTransfers;
  if (!succeeded || typeof succeeded !== "object" || Array.isArray(succeeded)) {
    return true;
  }
  const counts = Object.values(succeeded);
  if (counts.length === 0) {
    return false;
  }
  return counts.some((count) => {
    if (count === null || count === undefined) {
      return true;
    }
    const rows = Number(count);
    return !Number.isFinite(rows) || rows !== 0;
  });
};

// Both call sites resolve the toast copy through here so the key paths are spelled once. A
// partial transfer has already committed the fields that succeeded, so it must not report that
// nothing changed. Returns null when the error is not a transfer failure.
export const getTransferErrorTranslationKey = (
  error: unknown
): string | null => {
  if (!(error instanceof TransferFailedError)) {
    return null;
  }
  return error.movedAnyRecords
    ? `${TRANSLATION_KEY}.transferPartiallyFailed`
    : `${TRANSLATION_KEY}.transferFailed`;
};

// A mixed selection still offers Deactivate, and the selected ids go on to become the transfer's
// fromIds — so an already-inactive user left in the selection would have their records reassigned
// as a side effect of deactivating someone else.
export const selectUsersToDeactivate = (
  selectedUsers: UserData[]
): UserData[] =>
  selectedUsers.filter((user) => user.status === UserStatus.ACTIVE);

// The server rejects role and status changes for these users, whose identity provider owns both;
// this mirrors its test so the controls can be disabled instead of failing on save.
export const isManagedByIdentityProvider = (
  user: Pick<UserData, "email">,
  provisioning?: UserProvisioningResponse
): boolean => {
  if (!provisioning?.enabled || !user.email) return false;
  const email = user.email.trim();
  const [localPart, domain, ...rest] = email.split("@");
  if (!localPart || !domain || rest.length > 0 || /\s/.test(email)) {
    return false;
  }
  const normalizedDomain = domain.toLowerCase().replace(/\.$/, "");
  if (!(provisioning.managedDomains ?? []).includes(normalizedDomain)) {
    return false;
  }
  return !(provisioning.excludedEmails ?? []).includes(
    `${localPart.toLowerCase()}@${normalizedDomain}`
  );
};
